/**
 * Slack-mentioned Jira keys and GitHub PRs that should appear as Radar sources
 * on the thread's WorkThread without merging sibling Jira hubs.
 */

import {
  extractGitHubPullRequestMentions,
  extractJiraTicketKeys,
  parseGitHubPullRequestId,
  type GitHubPullRequestMention,
} from '@zunelo/shared';
import { githubItemLinksTicket } from './crossCards';
import type { AttentionItem } from './types';
import { assignItemToGroup, type TicketGroup } from './workThreadBuilders';

function mentionCorpus(item: AttentionItem): Array<string | undefined> {
  return [
    item.ticket,
    item.title,
    item.ai,
    item.desc,
    ...(item.refs ?? []).map((ref) => ref.key),
    ...(item.messages ?? []).map((message) => message.text),
  ];
}

function groupItemIds(group: TicketGroup): Set<string> {
  return new Set(
    [
      ...group.jira,
      ...group.github,
      ...group.slack,
      ...group.gmail,
      ...group.otherItems,
    ].map((entry) => entry.id),
  );
}

function githubMatchesMention(item: AttentionItem, mention: GitHubPullRequestMention): boolean {
  const parsed = parseGitHubPullRequestId(item.id);
  if (
    parsed?.owner &&
    parsed.repo &&
    parsed.number === mention.number &&
    parsed.owner.toLowerCase() === mention.owner.toLowerCase() &&
    parsed.repo.toLowerCase() === mention.repo.toLowerCase()
  ) {
    return true;
  }
  return githubHtmlUrlMatchesPull(item.htmlUrl, mention);
}

function githubHtmlUrlMatchesPull(
  htmlUrl: string | undefined,
  mention: GitHubPullRequestMention,
): boolean {
  if (!htmlUrl) return false;
  try {
    const parsed = new URL(htmlUrl);
    if (parsed.hostname.toLowerCase() !== 'github.com') return false;
    const parts = parsed.pathname.split('/').filter(Boolean);
    return (
      parts.length >= 4 &&
      parts[0].toLowerCase() === mention.owner.toLowerCase() &&
      parts[1].toLowerCase() === mention.repo.toLowerCase() &&
      parts[2].toLowerCase() === 'pull' &&
      parts[3] === String(mention.number)
    );
  } catch {
    return false;
  }
}

function shouldAttachGitHub(
  item: AttentionItem,
  keys: readonly string[],
  mentions: readonly GitHubPullRequestMention[],
): boolean {
  if (item.source !== 'github') return false;
  if (keys.some((key) => githubItemLinksTicket(item, key))) return true;
  return mentions.some((mention) => githubMatchesMention(item, mention));
}

function primarySlackTicket(slackItems: readonly AttentionItem[], keys: readonly string[]): string | undefined {
  const ticketed = slackItems.find((item) => item.ticket?.trim())?.ticket?.trim();
  return ticketed || keys[0];
}

/**
 * Add loaded (or stub) Jira/GitHub members that a Slack thread discusses.
 * Extra Jira hubs stay separate — only GitHub + the Slack primary ticket attach.
 */
export function attachSlackMentionedSources(
  group: TicketGroup,
  allItems: readonly AttentionItem[],
): void {
  if (group.slack.length === 0) return;

  const corpus = group.slack.flatMap(mentionCorpus);
  const keys = extractJiraTicketKeys(...corpus);
  const mentions = extractGitHubPullRequestMentions(...corpus);
  const present = groupItemIds(group);

  for (const item of allItems) {
    if (present.has(item.id)) continue;
    if (!shouldAttachGitHub(item, keys, mentions)) continue;
    assignItemToGroup(group, item);
    present.add(item.id);
  }

  if (group.jira.length > 0) return;
  const hub = primarySlackTicket(group.slack, keys);
  if (!hub) return;
  const jira = allItems.find(
    (item) => item.source === 'jira' && (item.ticket === hub || item.id === hub),
  );
  if (jira) {
    if (present.has(jira.id)) return;
    assignItemToGroup(group, jira);
    return;
  }
  group.impliedJiraKey = hub;
}
